import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessibleKanbans } from '@/hooks/useAccessibleKanbans';
import { useKanbanStore } from '@/stores/kanbanStore';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { 
  Building2,
  Scale,
  Crown,
  Star,
  CheckCircle
} from 'lucide-react';

interface KanbanSelectorProps {
  isCollapsed?: boolean;
}

const getKanbanIcon = (nome: string) => {
  if (nome.toLowerCase().includes('juridico')) return Scale;
  if (nome.toLowerCase().includes('vip')) return Crown;
  if (nome.toLowerCase().includes('especial')) return Star;
  if (nome.toLowerCase().includes('padrão')) return CheckCircle;
  return Building2;
};

const getKanbanColor = (cor: string) => {
  return cor || '#3b82f6';
};

export const KanbanSelector: React.FC<KanbanSelectorProps> = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const { data: kanbans = [], isLoading } = useAccessibleKanbans();
  const { selectedKanbanId, setSelectedKanbanId } = useKanbanStore();

  const handleKanbanSelect = (kanbanId: string) => {
    console.log('🔍 KanbanSelector - Selecionando kanban:', kanbanId);
    setSelectedKanbanId(kanbanId);
    navigate('/kanban');
  };

  if (isLoading) {
    return (
      <div className="px-2 py-1">
        <div className="h-8 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (isCollapsed) {
    return (
      <div className="px-2 py-1 space-y-1">
        {kanbans.map((kanban) => {
          const isSelected = kanban.id === selectedKanbanId;
          return (
            <Button
              key={kanban.id}
              variant="ghost"
              size="sm"
              className={cn(
                "w-full justify-center",
                isSelected && "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100"
              )}
              onClick={() => handleKanbanSelect(kanban.id)}
              title={kanban.nome}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: getKanbanColor(kanban.cor) }}
              />
            </Button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="px-2 py-1">
      <div className="space-y-1">
        {/* Lista de Kanbans - Exibidos separadamente, sem hierarquia */}
        {kanbans.map((kanban) => {
          const IconComponent = getKanbanIcon(kanban.nome);
          const isSelected = kanban.id === selectedKanbanId;
          const isActive = kanban.isAtivo;

          return (
            <Button
              key={kanban.id}
              variant="ghost"
              className={cn(
                "w-full justify-start h-auto p-2 text-left hover:bg-amber-100/80 dark:hover:bg-amber-500/10",
                isSelected && "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100"
              )}
              onClick={() => handleKanbanSelect(kanban.id)}
            >
              <div className="flex items-center space-x-2 w-full">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getKanbanColor(kanban.cor) }}
                />
                <IconComponent className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {kanban.nome}
                  </div>
                  <div className="flex items-center space-x-1">
                    <Badge
                      variant={isActive ? "default" : "secondary"}
                      className="text-xs px-1 py-0"
                    >
                      {isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                    {kanban.isDefault && (
                      <Badge variant="outline" className="text-xs px-1 py-0">
                        Padrão
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
